import { Col, Form, Input, Modal, Row, notification } from 'antd';
import React, { useState } from 'react';

import dataOperationClient from 'clients/dataOperation';
import DefaultButton from 'components/atoms/DefaultButton';
import DefaultForm from 'components/composites/DefaultForm';
import DefaultFormItem from 'components/composites/DefaultFormItem';

import DeviceBasedSamplePointSelect from './DeviceBasedSamplePointSelect';

const JIRA_TICKET_REGEX = /^(FMBT|MFB)-\d+$/;

type TransferFormValues = {
  jiraTicketId: string;
  sourceSamplePointId: string;
  targetSamplePointId: string;
};

function TransferTabContent(): JSX.Element {
  const [form] = Form.useForm<TransferFormValues>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleTransfer = (values: TransferFormValues) => {
    if (values.sourceSamplePointId === values.targetSamplePointId) {
      Modal.error({
        title: 'Validation Error',
        content: 'Source and target sample points cannot be the same'
      });
      return;
    }

    Modal.confirm({
      title: 'Confirm Transfer',
      content: (
        <div className="vertical-gap-1em">
          <div className="body-copy">Are you sure you want to transfer data?</div>
          <div className="body-copy">
            <span className="body-emphasis-copy">Jira Ticket:</span> {values.jiraTicketId}
          </div>
          <div className="body-copy">
            <span className="body-emphasis-copy">From Sample Point ID:</span> {values.sourceSamplePointId}
          </div>
          <div className="body-copy">
            <span className="body-emphasis-copy">To Sample Point ID:</span> {values.targetSamplePointId}
          </div>
        </div>
      ),
      okText: 'Transfer',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setIsSubmitting(true);
        try {
          const { data, error } = await dataOperationClient.createDataTransfer({
            jiraTicketId: values.jiraTicketId,
            sourceSamplePointSid: values.sourceSamplePointId,
            targetSamplePointSid: values.targetSamplePointId
          });

          if (error) {
            Modal.error({
              title: 'Transfer Failed',
              content: error.message || 'An error occurred while creating the data transfer operation'
            });
            return;
          }

          if (data) {
            notification.success({
              message: 'Data Transfer Initiated',
              description: `Operation ${data.operationUuid} has been created and will be processed shortly`,
              duration: 5
            });
            form.resetFields();
          }
        } catch (error) {
          Modal.error({
            title: 'Transfer Failed',
            content: 'An unexpected error occurred. Please try again'
          });
        } finally {
          setIsSubmitting(false);
        }
      }
    });
  };

  return (
    <DefaultForm
      form={form}
      layout="vertical"
      onFinish={handleTransfer}
    >
      <Row>
        <Col span={12}>
          <DefaultFormItem
            label="Jira Ticket ID"
            name="jiraTicketId"
            normalize={(value) => value?.toUpperCase()}
            rules={[
              { required: true, message: 'Jira Ticket ID is required' },
              {
                pattern: JIRA_TICKET_REGEX,
                message: 'Must be in format FMBT-xxxx or MFB-xxxx (e.g. FMBT-1234, MFB-567)'
              }
            ]}
          >
            <Input placeholder="e.g. FMBT-1234 or MFB-567" />
          </DefaultFormItem>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={12}>
          <DefaultFormItem
            label="Source Device"
            name="sourceSamplePointId"
            rules={[{ required: true, message: 'Please select a source sample point' }]}
          >
            <DeviceBasedSamplePointSelect />
          </DefaultFormItem>
        </Col>

        <Col span={12}>
          <DefaultFormItem
            label="Target Device"
            name="targetSamplePointId"
            dependencies={['sourceSamplePointId']}
            rules={[
              { required: true, message: 'Please select a target sample point' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('sourceSamplePointId') !== value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Target sample point must be different from source sample point'));
                }
              })
            ]}
          >
            <DeviceBasedSamplePointSelect />
          </DefaultFormItem>
        </Col>
      </Row>

      <Row>
        <Col span={24} style={{ textAlign: 'right' }}>
          <DefaultFormItem>
            <DefaultButton type="primary" htmlType="submit" loading={isSubmitting}>
              Transfer
            </DefaultButton>
          </DefaultFormItem>
        </Col>
      </Row>
    </DefaultForm>
  );
}

export default TransferTabContent;
